/**
 * useRadarPage drives a single page of /api/radar/relevant-clusters.
 *
 * Filters/sort/pagination live on the server now. The hook keeps a stable
 * cache key per query and uses radarQuery for stale-while-revalidate.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/apiService';
import { ApiError } from '../errors/ApiError';
import { NewsCluster } from '../types/types';
import { useRadarQuery } from './radarQuery';

export type RadarSortField = 'published_at' | 'article_count' | 'country';
export type RadarSortOrder = 'asc' | 'desc';

/** Cluster kinds — `clustered` for Clusters tab; `manual_source`/`single_article` for Articles tab. */
export type RadarClusterKind = 'clustered' | 'manual_source' | 'single_article';

export type RadarQuery = {
  profileId: string | null;
  /** Cluster kinds to include. Empty/undefined means all. */
  types?: RadarClusterKind[];
  page: number;          // 1-based
  pageSize: number;
  /** Free-text search applied across title/overview/summary/keywords. */
  q?: string;
  countries?: string[];
  geoScopes?: Array<'local' | 'regional' | 'international'>;
  entryTypes?: string[];
  issueTypes?: string[];
  contentCategories?: string[];
  minArticles?: number;
  maxArticles?: number;
  sort?: RadarSortField | null;
  order?: RadarSortOrder | null;
  lookbackDays?: number;
};

export type RadarListResponse = {
  items: NewsCluster[];
  total: number;
  generated_at: string;
};

/** Stable cache key — only the values that affect the response. */
function buildCacheKey(q: RadarQuery): ReadonlyArray<string | number | null> {
  return [
    'radar',
    q.profileId,
    (q.types ?? []).slice().sort().join(','),
    q.page,
    q.pageSize,
    q.q ?? '',
    (q.countries ?? []).slice().sort().join(','),
    (q.geoScopes ?? []).slice().sort().join(','),
    (q.entryTypes ?? []).slice().sort().join(','),
    (q.issueTypes ?? []).slice().sort().join(','),
    (q.contentCategories ?? []).slice().sort().join(','),
    q.minArticles ?? 0,
    q.maxArticles ?? 0,
    q.sort ?? null,
    q.order ?? null,
    q.lookbackDays ?? 3,
  ];
}

/** Convert a RadarQuery into the params object expected by /api/radar/relevant-clusters. */
function buildParams(q: RadarQuery): Record<string, unknown> {
  const params: Record<string, unknown> = {
    profile_id: q.profileId,
    lookback_days: q.lookbackDays ?? 3,
    limit: q.pageSize,
    offset: Math.max(0, (q.page - 1) * q.pageSize),
  };
  if (q.types && q.types.length) params.type = q.types.join(',');
  if (q.q) params.q = q.q;
  if (q.countries && q.countries.length) params.country = q.countries.join(',');
  if (q.geoScopes && q.geoScopes.length) params.geo_scope = q.geoScopes.join(',');
  if (q.entryTypes && q.entryTypes.length) params.entry_type = q.entryTypes.join(',');
  if (q.issueTypes && q.issueTypes.length) params.issue_type = q.issueTypes.join(',');
  if (q.contentCategories && q.contentCategories.length) params.content_category = q.contentCategories.join(',');
  if (q.minArticles && q.minArticles > 0) params.min_articles = q.minArticles;
  if (q.maxArticles && q.maxArticles > 0) params.max_articles = q.maxArticles;
  if (q.sort) {
    params.sort = q.sort;
    params.order = q.order ?? 'desc';
  }
  return params;
}

export type UseRadarPageResult = {
  items: NewsCluster[];
  total: number;
  generatedAt: string | null;
  isLoading: boolean;
  /** A response for the current query has been received (false while the first fetch is in flight). */
  hasData: boolean;
  error: unknown;
  pageCount: number;
  refetch: () => void;
};

/**
 * Hook entry point. Pass a RadarQuery; get paged data back.
 */
export function useRadarPage(query: RadarQuery): UseRadarPageResult {
  const enabled = Boolean(query.profileId);
  const key = useMemo(() => buildCacheKey(query), [query]);
  const params = useMemo(() => buildParams(query), [query]);

  const { data, error, isLoading, refetch } = useRadarQuery<RadarListResponse>(
    key,
    async () => {
      const res = (await api.get('/api/radar/relevant-clusters', { params })) as
        | RadarListResponse
        | NewsCluster[]
        | { clusters?: NewsCluster[] };

      // Tolerant of legacy shapes during rollout: backend changes to
      // { items, total, generated_at } but we keep guards so a stale backend
      // doesn't blank the UI.
      if (Array.isArray(res)) {
        return { items: res, total: res.length, generated_at: '' };
      }
      if (Array.isArray((res as { items?: unknown }).items)) {
        return res as RadarListResponse;
      }
      if (Array.isArray((res as { clusters?: unknown }).clusters)) {
        const items = (res as { clusters: NewsCluster[] }).clusters;
        return { items, total: items.length, generated_at: '' };
      }
      return { items: [], total: 0, generated_at: '' };
    },
    { enabled, ttlMs: 60_000 },
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = total > 0 ? Math.max(1, Math.ceil(total / query.pageSize)) : 1;

  return {
    items,
    total,
    generatedAt: data?.generated_at || null,
    isLoading,
    hasData: data !== undefined,
    error,
    pageCount,
    refetch,
  };
}

/** HTTP status of a failed request: ApiError/NotFoundError (axiosInstance) or a raw axios error. */
const httpStatus = (e: unknown): number | undefined => {
  if (e instanceof ApiError) return e.status;
  return (e as { response?: { status?: number } } | null)?.response?.status;
};

export type UseRadarClusterResult = {
  cluster: NewsCluster | null;
  /** The server answered 404: the cluster doesn't exist for this profile / lookback window. */
  notFound: boolean;
  /** Any other failure (network, 5xx): the cluster may still exist. */
  failed: boolean;
  isLoading: boolean;
};

/**
 * One cluster by id (GET /api/radar/relevant-clusters/:id). Used for deep links whose cluster isn't on
 * the page the link points at (e.g. newer clusters pushed it to a later page).
 */
export function useRadarCluster(
  clusterId: string | null,
  profileId: string | null,
  lookbackDays: number | undefined,
  enabled: boolean,
): UseRadarClusterResult {
  const on = enabled && Boolean(clusterId) && Boolean(profileId);
  const { data, error, isFetching } = useQuery({
    queryKey: ['radar-cluster', profileId, clusterId, lookbackDays ?? 3],
    queryFn: async () =>
      (await api.get(`/api/radar/relevant-clusters/${encodeURIComponent(clusterId ?? '')}`, {
        params: { profile_id: profileId, lookback_days: lookbackDays ?? 3 },
      })) as NewsCluster,
    enabled: on,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // A 404 is an answer, not a transient failure.
    retry: (count, e) => httpStatus(e) !== 404 && count < 1,
  });
  const valid = data && typeof data === 'object' && typeof (data as NewsCluster).title === 'string' ? data : null;
  return {
    cluster: on ? valid : null,
    // A 200 that isn't a cluster (legacy/empty payload) is treated as missing too.
    notFound: on && ((!!error && httpStatus(error) === 404) || (data !== undefined && !valid)),
    failed: on && !!error && httpStatus(error) !== 404,
    isLoading: on && isFetching && !data,
  };
}
